import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { InputLabelComponent } from 'src/app/ui-kit/form-controls/input-label/input-label.component';

/**
 * Shared form-field wrapper that composes label, required indicator, helper text,
 * error text, and a projected control into a single consistent layout.
 *
 * Every custom form control in the ui-kit should wrap its input element with this
 * component so label/helper/error styling is identical across the app. The control
 * itself is projected via `<ng-content>`, keeping this component presentation-only
 * (no `ControlValueAccessor` — the projected control provides that).
 *
 * When `errorText` is present it replaces `helperText` visually and is announced via
 * `aria-live="polite"`. Pass the same `fieldId` to both this wrapper (`for`) and the
 * projected control's `id` attribute so the label is correctly associated.
 *
 * @example
 * <app-form-field label="Email" fieldId="email" [required]="true" [errorText]="emailError()">
 *   <input id="email" type="email" [formControl]="emailCtrl" class="..." />
 * </app-form-field>
 *
 * @feature shared
 */
@Component({
  selector: 'app-form-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputLabelComponent],
  host: { class: 'block' },
  template: `
    @if (label()) {
      <app-input-label [for]="fieldId()" [required]="required()" [size]="labelSize()">
        {{ label() }}
      </app-input-label>
    }

    <div class="mt-1">
      <ng-content />
    </div>

    @if (description()) {
      <p class="mt-1 text-sm text-muted">{{ description() }}</p>
    }

    @if (hasError()) {
      <p class="mt-1 text-sm text-danger" role="alert" aria-live="polite">
        {{ errorText() }}
      </p>
    } @else if (helperText()) {
      <p class="mt-1 text-sm text-muted">{{ helperText() }}</p>
    }
  `,
})
export class FormFieldComponent {
  /** Visible label text. Omit to render a label-less field. */
  readonly label = input<string | undefined>(undefined);

  /** Long-form descriptive text rendered between label and helper/error. */
  readonly description = input<string | undefined>(undefined);

  /**
   * The `for` attribute on the label and the `id` of the projected control. Required
   * for label/control association and screen-reader support.
   */
  readonly fieldId = input<string | undefined>(undefined);

  /** Marks the field as required and shows the red asterisk. @default false */
  readonly required = input(false);

  /** Neutral helper text shown below the control. Hidden when `errorText` is set. */
  readonly helperText = input<string | undefined>(undefined);

  /** Error message. When set, replaces helper text and is announced via `aria-live`. */
  readonly errorText = input<string | undefined>(undefined);

  /** Label typographic size. @default 'md' */
  readonly labelSize = input<'sm' | 'md' | 'lg'>('md');

  /** True when a non-empty error message is present. */
  readonly hasError = computed(() => !!this.errorText()?.trim());
}
